import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { PromoIcon } from 'apps/promo/components/common/promo-icon/PromoIcon';
import { PromoText } from 'apps/promo/components/common/promo-text/PromoText';
import { SearchIconWrap } from 'apps/promo/components/form/PromoInputPhoneWithDropdown/PromoInputSearch';
import PromoInputWrapper from 'apps/promo/components/form/PromoInputWrapper';
import { getArticles } from 'apps/promo/components/help/utils';
import { getHelpCenterUrl, useGetQueryParams } from 'apps/promo/components/utils';
import { logInfo } from 'apps/promo/helpers/clientLogger';
import { getLangCountryPrefix } from 'apps/promo/helpers/utils';
import { useHandleCloseDropdownMenu } from 'apps/promo/hooks/useHandleCloseDropdownMenu';

import { SearchDropdown, SearchDropdownItem, SearchWrapper } from './styles';
import { PromoRow } from '../common/PromoRow/PromoRow';

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value]);

    return debouncedValue;
};

const ZendeskHelpSearch = (props) => {
    const wrapperRef = useRef(null);

    const [search, setSearch] = useState('');
    const [foundArticles, setFoundArticles] = useState([]);

    const [isOpenDropdownMenu, setOpenDropdownMenu] = useHandleCloseDropdownMenu(wrapperRef);

    const url = props.isHelpMobile
        ? `${getLangCountryPrefix(props.lang, props.country)}/help-mobile`
        : `${getLangCountryPrefix(props.lang, props.country)}/help`;
    const urlParams = useGetQueryParams();

    const focus = (e) => {
        if (e.currentTarget) {
            const input = e.currentTarget.firstElementChild;
            input.focus();
        } else {
            logInfo('Focus method not support');
        }
    };

    const debouncedSearch = useDebounce(search, 1000);

    useEffect(() => {
        const fetchData = async (query) => {
            if (!query) {
                return;
            }

            let articles = await getArticles(props.lang, props.country, query, props.sections);
            articles = articles?.filter((article) => !article.directLink);

            if (articles) {
                setFoundArticles(articles);
                setOpenDropdownMenu(true);
            }
        };

        fetchData(debouncedSearch);
    }, [debouncedSearch]);

    const articlesNames = foundArticles?.map((article) => {
        return (
            <SearchDropdownItem
                key={article.id}
                href={getHelpCenterUrl(`${url}/category/${article.category_id}/section/${article.section_id}/article/${article.id}`, urlParams)}
            >
                {article.title}
            </SearchDropdownItem>
        );
    });

    return (
        <PromoRow indent="adaptive-32-48-64">
            <SearchWrapper ref={wrapperRef}>
                <PromoInputWrapper
                    name="search"
                    setFocus={focus}
                    leftInputComponent={
                        <SearchIconWrap>
                            <PromoIcon name="search" color="gray80" size={'24px'} />
                        </SearchIconWrap>
                    }
                >
                    <input type="text" value={search} placeholder={props.searchPlaceholder} onChange={(e) => setSearch(e.target.value)} />
                </PromoInputWrapper>
                {isOpenDropdownMenu && (
                    <>
                        {articlesNames?.length === 0 && search && (
                            <SearchDropdown centerAligned>
                                <PromoText tag={'div'} textAlign={'center'} color={'gray40'}>
                                    {props.searchNotFound}
                                </PromoText>
                            </SearchDropdown>
                        )}
                        {articlesNames?.length > 0 && search && <SearchDropdown>{articlesNames}</SearchDropdown>}
                    </>
                )}
            </SearchWrapper>
        </PromoRow>
    );
};

const mapStateToProps = ({ extra }) => {
    return {
        sections: extra?.zendeskHelpData?.sections,
        searchPlaceholder: extra?.strapiApi?.search_placeholder,
        searchNotFound: extra?.strapiApi?.search_not_found,
        lang: extra.noAuthFooter?.language?.lang,
        country: extra.noAuthFooter?.language?.country,
    };
};

export default connect(mapStateToProps, null)(ZendeskHelpSearch);
